import type SearchPrediction from 'data/search-predictions';

/**
 * Removes duplicates from a list of search predictions based on description
 * and preserves location IDs from Google predictions.
 * 
 * @param predictions - Array of search predictions
 * @returns Deduplicated array of search predictions
 */
export function uniqByDescriptionAndAddLocationId(predictions: SearchPrediction[]): SearchPrediction[] {
  const resultMap: {[description: string]: SearchPrediction } = {};
  
  for (const prediction of predictions) {
    const descriptionKey = prediction.description.toLowerCase();
    if (descriptionKey in resultMap) {
      const existingLocation = resultMap[descriptionKey];
      if (prediction.source === 'google') {
        existingLocation.locationId = prediction.id;
        existingLocation.source = prediction.source;
      }
    } else {
      resultMap[descriptionKey] = { ...prediction } as SearchPrediction;
    }
  }
  
  return Object.values(resultMap);
}

/**
 * Adds a new item to the top of an array, removes duplicates by a key, and enforces a maximum length.
 * 
 * @param currentItems - Current array of items
 * @param newItem - New item to add to the top
 * @param getKey - Function to extract the key for duplicate detection
 * @param maxItems - Maximum number of items to keep (default: 12)
 * @returns New array with the item added to the top, duplicates removed, and max length enforced
 */
export function addToTopWithDeduplication<T>(
  currentItems: T[] | undefined,
  newItem: T,
  getKey: (item: T) => string,
  maxItems: number = 12
): T[] {
  if (!currentItems || currentItems.length === 0) {
    return [newItem];
  }

  // Remove any existing item with the same key
  const filteredItems = currentItems.filter(
    item => getKey(item) !== getKey(newItem)
  );

  // If we're at max capacity after filtering, remove the last item
  if (filteredItems.length === maxItems) {
    filteredItems.pop();
  }

  // Add the new item at the top
  return [newItem, ...filteredItems];
}

/**
 * Determines if a prediction array has location results.
 * 
 * @param searchPredictions - Array of search predictions
 * @returns True if the array contains location predictions
 */
export function areLocationsPresentInResult(searchPredictions: SearchPrediction[]): boolean {
  return searchPredictions.some(prediction => prediction.group === 'locations');
} 